import { Controller } from "@hotwired/stimulus";
import tinymce from "tinymce/tinymce";

// Import Plugins
import "tinymce/plugins/link";
import "tinymce/plugins/autolink";
import "tinymce/plugins/advlist";
import "tinymce/plugins/lists";
import "tinymce/plugins/charmap";
import "tinymce/plugins/anchor";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/visualchars";
import "tinymce/plugins/nonbreaking";
import "tinymce/plugins/contextmenu";
import "tinymce/plugins/directionality";
import "tinymce/plugins/paste";
import "tinymce/plugins/textpattern";
import "tinymce/plugins/autoresize";

// Import Theme & Icons
import "tinymce/themes/silver/theme";
import "tinymce/icons/default";

export default class TinymceConsentController extends Controller {

  connect() {
    this.initializeTinyMCE();
  }

  disconnect() {
    this.destroyTinyMCE();
  }

  destroyTinyMCE() {
    tinymce.remove();
  }

  initializeTinyMCE() {
    if (tinymce.editors.length > 0) {
      this.destroyTinyMCE();
    }

    const interpolations = [
      { key: 'assessor.full_name', label: "Assessor's Full Name" },
      { key: 'assessor.email', label: "Assessor's Email" },
      { key: 'assessor.phone', label: "Assessor's Phone" },
      { key: 'date.current', label: "Current Date" },
    ];

    let variables = [];

    tinymce.init({
      target: this.element,
      relative_urls: false,
      menubar: false,
      resize: false,
      toolbar: " undo redo | formatselect | bold italic | link unlink | alignleft aligncenter alignright alignjustify | outdent indent | interpolate variables",
      plugins: "link,autolink,autoresize,paste",
      block_formats: 'Paragraph=p;Heading 1=h1;Heading 2=h2;Heading 3=h3;Heading 4=h4;',
      min_height: 600,
      content_style: `
        span[data-interpolate], span[data-variable] { 
          background: #f64747;
          color: white;
          font-weight: bold; 
          pointer-events: none;
          padding: 4px 6px;
          border-radius: 4px;
        }
        span[data-variable] {
          background: #1a1a1a;
        }
        span[data-interpolate]::before, 
        span[data-interpolate]::after,
        span[data-variable]::before,
        span[data-variable]::after {
          content: '\\200B';  /* Zero-width space */
        }
      `, // Add your custom CSS here
      setup: function(editor) {
        // Interpolate button
        editor.ui.registry.addMenuButton('interpolate', {
          text: 'Interpolate',
          fetch: function(callback) {
            const items = interpolations.map((interpolation) => ({
              type: 'menuitem',
              text: interpolation.label,
              onAction: function() {
                editor.insertContent(`<span data-interpolate="${interpolation.key}" contenteditable="false">[${interpolation.label.toUpperCase()}]</span>`);
              }
            }));
            callback(items);
          }
        });

        // Variables button
        editor.ui.registry.addButton('variables', {
          text: 'Variables',
          onAction: function() {
            // Open a dialog to define a new variable
            editor.windowManager.open({
              title: 'Define Variable',
              body: {
                type: 'panel',
                items: [
                  {
                    type: 'htmlpanel', // Use htmlpanel to add static text
                    html: '<p style="margin-bottom: 10px; color: #666; font-size: 12px;">Define a variable that assessors must provide during the signing process to interpolate in to the document text.</p>'
                  },
                  {
                    type: 'input',
                    name: 'name',
                    label: 'Variable Name',
                    placeholder: 'Enter variable name...'
                  },
                  {
                    type: 'listbox',
                    name: 'type',
                    label: 'Data Type',
                    items: [
                      { text: 'Text', value: 'text' },
                      { text: 'Date', value: 'date' }
                    ]
                  }
                ]
              },
              buttons: [
                {
                  type: 'cancel',
                  text: 'Cancel'
                },
                {
                  type: 'submit',
                  text: 'Save',
                  primary: true
                }
              ],
              onSubmit: function(api) {
                const data = api.getData();
                const name = data.name;
                const type = data.type;

                if (name && type) {
                  // Add the variable to the list
                  variables.push({ name, type });

                  // Insert the variable into the editor
                  editor.insertContent(`<span data-variable data-name="${name}" data-type="${type}" contenteditable="false">${name} [${type.toUpperCase()}]</span>`);

                  api.close();
                }
              }
            });
          }
        });
      }
    });
  }
}
